import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../services/api';
import { decrypt } from '../utils/security';

const C = {
  rose: '#C97B77', rosePale: '#F9EDEC', roseLt: '#E8B4B1',
  slate: '#6B7FA0', slatePale: '#EEF2F8', slateLt: '#A8B8D0',
  sage: '#7A9E8E', ink: '#2C2C35', inkMid: '#555567', inkLt: '#888899',
  border: '#E8E4DF', white: '#FFFFFF', offWhite: '#FAFAF8', pageBg: '#F5F2EE',
  amber: '#C4975A', amberPale: '#FBF4E8',
};

export default function BookCategoryPage() {
  const [books, setBooks] = useState([]);
  const [pagination, setPag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState(null);

  const [categories, setCategories] = useState([]);
  const [topics, setTopics] = useState([]);

  const [selectedCat, setSelectedCat] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');

  const [showAddBook, setShowAddBook] = useState(false);
  const [showAddSubj, setShowAddSubj] = useState(null); // 'categories' | 'topics'
  const [newSubjName, setNewSubjName] = useState('');

  const [newBook, setNewBook] = useState({ id: '', name: '', title: '', category: '', topics: [] });

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [bookRes, subjRes] = await Promise.all([
        adminAPI.getBooks({ page, limit: 20, search, category: selectedCat, topic: selectedTopic }),
        adminAPI.getSubjects()
      ]);
      setBooks(bookRes.data || []);
      setPag(bookRes.pagination);
      setCategories(subjRes.data.categories || []);
      setTopics(subjRes.data.topics || []);
    } catch (e) { showToast(e.message, 'error'); }
    finally { setLoading(false); }
  }, [page, search, selectedCat, selectedTopic]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAddSubject = async () => {
    if (!newSubjName.trim()) return;
    try {
      await adminAPI.addSubject(showAddSubj, newSubjName.trim());
      showToast(`${showAddSubj === 'categories' ? 'Category' : 'Topic'} added ✓`);
      setNewSubjName('');
      setShowAddSubj(null);
      loadData();
    } catch (e) { showToast(e.message, 'error'); }
  };

  const handleAddBook = async () => {
    if (!newBook.name || !newBook.category) {
      return showToast('Book name and category required', 'error');
    }
    try {
      await adminAPI.addBook({
        _id: newBook.id || undefined,
        book_name: newBook.name,
        book_title: newBook.title || newBook.name,
        category: newBook.category,
        topics: newBook.topics.map(t => ({ name: t }))
      });
      showToast('Book added successfully ✓');
      setShowAddBook(false);
      setNewBook({ id: '', name: '', title: '', category: '', topics: [] });
      loadData();
    } catch (e) { showToast(e.message, 'error'); }
  };

  const handleUpdateBook = async (bookId, field, value) => {
    try {
      // If it's topics, handle the array appropriately
      await adminAPI.updateBook(bookId, { [field]: value });
      setBooks(prev => prev.map(b => b._id === bookId ? {
        ...b,
        [field]: field === 'category' ? { name: value } : (field === 'topics' ? value.map(n => ({ name: n })) : value)
      } : b));
      showToast('Updated ✓');
    } catch (e) { showToast(e.message, 'error'); }
  };

  const toggleBookTopic = (book, topicName) => {
    const current = (book.topics || []).map(t => t.name);
    let next;
    if (current.includes(topicName)) {
      next = current.filter(t => t !== topicName);
    } else {
      next = [...current, topicName];
    }
    handleUpdateBook(book._id, 'topics', next);
  };

  return (
    <div style={{ padding: 32, fontFamily: 'Nunito,sans-serif' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ fontFamily: 'Playfair Display,serif', fontSize: 26, fontWeight: 700, color: C.ink }}>
            Category <em style={{ color: C.rose, fontStyle: 'italic' }}>& Topics</em>
          </div>
          <div style={{ fontSize: 13, color: C.inkLt, marginTop: 2 }}>
            Organize books by primary Category and Subcategory/Topics
          </div>
        </div>
        <button onClick={() => setShowAddBook(true)}
          style={{ padding: '10px 20px', background: C.rose, color: 'white', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px rgba(201,123,119,0.2)' }}>
          + Add New Book
        </button>
      </div>

      {/* Top Controls */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260, position: 'relative' }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 15, color: '#AAAABC' }}>🔍</span>
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search books by name…"
            style={{ width: '100%', padding: '10px 14px 10px 38px', borderRadius: 10, border: `2px solid ${C.border}`, fontSize: 13, fontFamily: 'Nunito,sans-serif', outline: 'none', boxSizing: 'border-box' }} />
        </div>

        {/* Filter Dropdowns */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <select value={selectedCat} onChange={e => { setSelectedCat(e.target.value); setPage(1); }}
            style={{ padding: '9px 28px 9px 12px', border: `2px solid ${C.border}`, borderRadius: 10, fontSize: 12, fontWeight: 700, outline: 'none', cursor: 'pointer', background: 'white', appearance: 'none', minWidth: 140 }}>
            <option value="">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {selectedCat && (
            <button onClick={() => { setSelectedCat(''); setPage(1); }}
              style={{ position: 'absolute', right: 8, background: 'none', border: 'none', color: C.inkLt, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
          )}
        </div>

        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <select value={selectedTopic} onChange={e => { setSelectedTopic(e.target.value); setPage(1); }}
            style={{ padding: '9px 28px 9px 12px', border: `2px solid ${C.border}`, borderRadius: 10, fontSize: 12, fontWeight: 700, outline: 'none', cursor: 'pointer', background: 'white', appearance: 'none', minWidth: 140 }}>
            <option value="">All SubCategories</option>
            {topics.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          {selectedTopic && (
            <button onClick={() => { setSelectedTopic(''); setPage(1); }}
              style={{ position: 'absolute', right: 8, background: 'none', border: 'none', color: C.inkLt, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
          )}
        </div>

        <button onClick={() => setShowAddSubj('categories')}
          style={{ padding: '9px 16px', background: C.white, color: C.inkMid, border: `2px solid ${C.border}`, borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
          📁 New Category
        </button>
        <button onClick={() => setShowAddSubj('topics')}
          style={{ padding: '9px 16px', background: C.white, color: C.inkMid, border: `2px solid ${C.border}`, borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
          🏷️ New SubCategory
        </button>
      </div>

      {/* Table */}
      <div style={{ background: 'white', borderRadius: 14, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 200px 200px', gap: 0, padding: '12px 16px', background: '#F5F2EE', borderBottom: `1px solid ${C.border}`, fontSize: 10, fontWeight: 700, color: C.inkLt, textTransform: 'uppercase', letterSpacing: 1.5 }}>
          <div>ID</div>
          <div>Book Name</div>
          <div>Category</div>
          <div>Subcategory / Topic</div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: C.inkLt }}>Loading data…</div>
        ) : books.map((book, i) => {
          const bookCat = book.category?.name || '';
          const bookTopic = book.topics?.[0]?.name || '';
          return (
            <div key={book._id} style={{ display: 'grid', gridTemplateColumns: '80px 1fr 200px 200px', gap: 0, padding: '12px 16px', borderBottom: `1px solid ${C.offWhite}`, alignItems: 'center', background: i % 2 === 0 ? 'white' : C.offWhite }}>
              <div style={{ fontSize: 11, color: C.inkLt }}>#{book._id}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{decrypt(book.book_name || book.book_title)}</div>

              <div>
                <select value={bookCat} onChange={e => handleUpdateBook(book._id, 'category', e.target.value)}
                  style={{ width: '90%', padding: '6px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 12, fontFamily: 'Nunito,sans-serif' }}>
                  <option value="">Select Category</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', maxWidth: '100%' }}>
                  {(book.topics || []).map(t => (
                    <div key={t.name} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      background: C.rosePale, color: C.rose,
                      fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 20,
                      border: `1px solid ${C.roseLt}`, transition: 'all 0.2s',
                      maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                    }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</span>
                      <span onClick={() => toggleBookTopic(book, t.name)}
                        style={{ cursor: 'pointer', opacity: 0.6, fontSize: 12, lineHeight: 1 }}>✕</span>
                    </div>
                  ))}
                  <div style={{ position: 'relative', maxWidth: '100%' }}>
                    <select value="" onChange={e => { if (e.target.value) toggleBookTopic(book, e.target.value); }}
                      style={{
                        padding: '4px 8px', fontSize: 10, fontWeight: 700, borderRadius: 20,
                        border: `1px solid ${C.border}`, background: 'white', color: C.inkLt,
                        cursor: 'pointer', outline: 'none', appearance: 'none', textAlign: 'center', minWidth: 80,
                        maxWidth: '100%', boxSizing: 'border-box'
                      }}>
                      <option value="">+ Add SubCategories</option>
                      {topics.filter(t => !(book.topics || []).some(bt => bt.name === t)).map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 20 }}>
          <button disabled={!pagination.has_prev} onClick={() => setPage(p => p - 1)}
            style={{ padding: '8px 16px', borderRadius: 8, border: `2px solid ${C.border}`, background: 'white', color: C.inkMid, fontSize: 12, fontWeight: 700, cursor: pagination.has_prev ? 'pointer' : 'not-allowed', opacity: pagination.has_prev ? 1 : .4 }}>
            ‹ Prev
          </button>
          <span style={{ fontSize: 12, color: C.inkLt }}>Page {page} of {pagination.total_pages}</span>
          <button disabled={!pagination.has_next} onClick={() => setPage(p => p + 1)}
            style={{ padding: '8px 16px', borderRadius: 8, border: `2px solid ${C.border}`, background: 'white', color: C.inkMid, fontSize: 12, fontWeight: 700, cursor: pagination.has_next ? 'pointer' : 'not-allowed', opacity: pagination.has_next ? 1 : .4 }}>
            Next ›
          </button>
        </div>
      )}

      {/* Modals */}
      {showAddSubj && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: 24, borderRadius: 16, width: 340, boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: C.ink }}>Add New {showAddSubj === 'categories' ? 'Category' : 'Topic'}</div>
            <input autoFocus value={newSubjName} onChange={e => setNewSubjName(e.target.value)}
              placeholder="Enter name…"
              style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 20, outline: 'none' }} />
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowAddSubj(null)} style={{ flex: 1, padding: '10px', borderRadius: 8, border: 'none', background: C.rosePale, color: C.rose, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleAddSubject} style={{ flex: 1, padding: '10px', borderRadius: 8, border: 'none', background: C.rose, color: 'white', fontWeight: 700, cursor: 'pointer' }}>Add</button>
            </div>
          </div>
        </div>
      )}

      {showAddBook && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: 24, borderRadius: 16, width: 400, boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 20, color: C.ink }}>Add New Book</div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.inkLt, marginBottom: 4 }}>NAME</label>
              <input value={newBook.name} onChange={e => setNewBook({ ...newBook, name: e.target.value })} placeholder="Book Name" style={{ width: '100%', padding: '9px', borderRadius: 8, border: `1px solid ${C.border}`, boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.inkLt, marginBottom: 4 }}>CATEGORY</label>
                <select value={newBook.category} onChange={e => setNewBook({ ...newBook, category: e.target.value })} style={{ width: '100%', padding: '9px', borderRadius: 8, border: `1px solid ${C.border}` }}>
                  <option value="">Select</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.inkLt, marginBottom: 4 }}>TOPICS (MULTIPLE)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px', border: `1px solid ${C.border}`, borderRadius: 8, background: C.offWhite, marginBottom: 8 }}>
                  {newBook.topics.map(t => (
                    <div key={t} style={{ background: C.rose, color: 'white', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                      {t} <span onClick={() => setNewBook({ ...newBook, topics: newBook.topics.filter(x => x !== t) })} style={{ cursor: 'pointer' }}>✕</span>
                    </div>
                  ))}
                </div>
                <select onChange={e => { if (e.target.value && !newBook.topics.includes(e.target.value)) setNewBook({ ...newBook, topics: [...newBook.topics, e.target.value] }) }} style={{ width: '100%', padding: '9px', borderRadius: 8, border: `2px solid ${C.border}` }}>
                  <option value="">+ Select Topics</option>
                  {topics.filter(t => !newBook.topics.includes(t)).map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button onClick={() => setShowAddBook(false)} style={{ flex: 1, padding: '10px', borderRadius: 8, border: 'none', background: C.rosePale, color: C.rose, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleAddBook} style={{ flex: 1, padding: '10px', borderRadius: 8, border: 'none', background: C.rose, color: 'white', fontWeight: 700, cursor: 'pointer' }}>Create Book</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, background: toast.type === 'error' ? C.rose : C.ink, color: 'white', padding: '12px 24px', borderRadius: 12, fontSize: 13, fontWeight: 700, boxShadow: '0 4px 20px rgba(0,0,0,.2)', whiteSpace: 'nowrap' }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
